/**
 * PageSections Component
 * 
 * Reusable component for rendering dynamic page sections from the database.
 * Supports accordion-style expansion with entity-specific styling.
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, BookOpen, Heart, Shield, Zap, Users, Eye, Star, AlertTriangle, Sparkles, Target, Clock, Scale, Crown, Activity, MessageCircle, Compass, Brain, Wrench, Mic, Film, Palette, Flame } from 'lucide-react';

// Master section configuration - covers all entity types
export const SECTION_CONFIG = {
    // ─── Universal ─────────────────────────────────────────
    overview: { icon: BookOpen, title: 'Overview', color: 'text-blue-400' },
    description: { icon: BookOpen, title: 'Overview', color: 'text-blue-400' },
    combinations: { icon: Sparkles, title: 'Pairs Well With', color: 'text-amber-400' },
    progression: { icon: Zap, title: "What's Next", color: 'text-indigo-400' },
    community: { icon: Users, title: 'Community & Spaces', color: 'text-blue-400' },
    experience_levels: { icon: Activity, title: 'Experience Levels', color: 'text-emerald-400' },
    variations: { icon: Zap, title: 'Variations & Twists', color: 'text-purple-400' },
    safety: { icon: Shield, title: 'Safety', color: 'text-red-400' },
    risks: { icon: AlertTriangle, title: 'Risks', color: 'text-red-400' },

    // ─── Safety ────────────────────────────────────────────
    accepted_risks: { icon: AlertTriangle, title: 'Risks You Accept', color: 'text-red-400' },
    aftercare: { icon: Heart, title: 'Aftercare', color: 'text-pink-400' },
    red_flags: { icon: AlertTriangle, title: 'Red Flags', color: 'text-red-500' },
    risks_emotional: { icon: Heart, title: 'Emotional Risks', color: 'text-rose-400' },
    consent_guide: { icon: Shield, title: 'Consent Framework', color: 'text-emerald-400' },
    common_mistakes: { icon: AlertTriangle, title: 'Common Mistakes', color: 'text-orange-400' },

    // ─── Discovery ─────────────────────────────────────────
    where_to_find: { icon: Eye, title: 'Where to Find Content', color: 'text-purple-400' },
    media: { icon: Film, title: 'In Pop Culture', color: 'text-amber-400' },
    media_representation: { icon: Film, title: 'Media Portrayal', color: 'text-amber-400' },
    porn_tags: { icon: Eye, title: 'Porn Search Terms', color: 'text-rose-400' },
    related_kinks: { icon: Sparkles, title: 'Related Kinks', color: 'text-purple-400' },
    related: { icon: Sparkles, title: 'Related Explorations', color: 'text-purple-400' },

    // ─── Physical ──────────────────────────────────────────
    physical_requirements: { icon: Activity, title: 'Physical Requirements', color: 'text-orange-400' },
    cultural_context: { icon: Users, title: 'Cultural Context', color: 'text-cyan-400' },
    solo_options: { icon: Star, title: 'Solo Play', color: 'text-pink-400' },

    // ─── Kink-specific ─────────────────────────────────────
    appeal: { icon: Flame, title: 'Why It Turns You On', color: 'text-rose-400' },
    activities: { icon: Zap, title: 'Techniques', color: 'text-amber-400' },
    mechanics: { icon: Target, title: 'Detailed How-To', color: 'text-blue-400' },
    advanced_techniques: { icon: Crown, title: 'Advanced Play', color: 'text-purple-400' },
    beginner_tips: { icon: Compass, title: 'Beginner Guide', color: 'text-emerald-400' },
    dirty_talk: { icon: Mic, title: 'Dirty Talk', color: 'text-rose-400' },
    dynamics: { icon: Crown, title: 'Power Dynamics', color: 'text-purple-400' },
    faq: { icon: MessageCircle, title: 'FAQ', color: 'text-blue-400' },
    gear: { icon: Wrench, title: 'Gear & Equipment', color: 'text-amber-400' },
    history: { icon: BookOpen, title: 'History', color: 'text-cyan-400' },
    social_stigma: { icon: Users, title: 'Social Stigma', color: 'text-orange-400' },
    preparation: { icon: Target, title: 'Preparation', color: 'text-blue-400' },
    atmosphere: { icon: Sparkles, title: 'Setting the Mood', color: 'text-purple-400' },
    coming_out: { icon: MessageCircle, title: 'Disclosure Guide', color: 'text-teal-400' },
    simulation_10_levels: { icon: Flame, title: '10 Intensity Levels', color: 'text-red-400' },
    empathy_simulation: { icon: Heart, title: 'Empathy Sim', color: 'text-pink-400' },
    scenarios_partnered: { icon: Users, title: 'Partnered Scenes', color: 'text-rose-400' },
    scenarios_solo: { icon: Star, title: 'Solo Scenes', color: 'text-pink-400' },
    roleplay_scripts: { icon: Mic, title: 'Roleplay Scripts', color: 'text-amber-400' },

    // ─── Sex Action-specific ───────────────────────────────
    anatomy_focus: { icon: Target, title: 'Key Anatomy', color: 'text-rose-400' },
    rhythm_guide: { icon: Clock, title: 'Rhythm & Pacing', color: 'text-emerald-400' },
    orgasm_optimization: { icon: Sparkles, title: 'Orgasm Tips', color: 'text-pink-400' },

    // ─── Gear-specific ─────────────────────────────────────
    size_guide: { icon: Scale, title: 'Size Guide', color: 'text-blue-400' },
    material_safety: { icon: Shield, title: 'Material Safety', color: 'text-green-400' },
    cleaning_guide: { icon: Sparkles, title: 'Cleaning Guide', color: 'text-cyan-400' },
    buying_guide: { icon: Compass, title: 'Buying Guide', color: 'text-blue-400' },
    budget_vs_premium: { icon: Scale, title: 'Budget vs Premium', color: 'text-amber-400' },
    materials: { icon: Shield, title: 'Material Science', color: 'text-green-400' },
    types: { icon: Zap, title: 'Types & Variations', color: 'text-purple-400' },
    how_to_use: { icon: Target, title: 'How to Use', color: 'text-blue-400' },
    diy: { icon: Wrench, title: 'DIY Alternatives', color: 'text-orange-400' },
    storage_travel: { icon: Compass, title: 'Storage & Travel', color: 'text-blue-400' },

    // ─── Archetype-specific ────────────────────────────────
    behaviors: { icon: Eye, title: 'Observable Behaviors', color: 'text-amber-400' },
    compatibility: { icon: Users, title: 'Compatibility', color: 'text-pink-400' },
    discovery: { icon: Compass, title: 'Self-Discovery', color: 'text-emerald-400' },
    evolution: { icon: Zap, title: 'Growth Over Time', color: 'text-indigo-400' },
    expression: { icon: Palette, title: 'Expression', color: 'text-purple-400' },
    psychology: { icon: Brain, title: 'Psychology', color: 'text-indigo-400' },
    shadow_side: { icon: AlertTriangle, title: 'The Dark Side', color: 'text-red-400' },
    growth_path: { icon: Zap, title: 'Growth Path', color: 'text-emerald-400' },
    negotiation_tips: { icon: MessageCircle, title: 'Negotiation Style', color: 'text-amber-400' },

    // ─── Dynamic-specific ──────────────────────────────────
    power_flow: { icon: Crown, title: 'Power Structure', color: 'text-purple-400' },
    daily_protocols: { icon: Clock, title: 'Daily Rituals', color: 'text-emerald-400' },
    exit_strategy: { icon: ChevronDown, title: 'Ending Gracefully', color: 'text-rose-400' },
    negotiation: { icon: Scale, title: 'Negotiation', color: 'text-amber-400' },
    maintenance: { icon: Heart, title: 'Maintenance', color: 'text-pink-400' },
    protocols: { icon: Clock, title: 'Protocols & Rituals', color: 'text-emerald-400' },
    roles: { icon: Users, title: 'Roles', color: 'text-blue-400' },
    challenges: { icon: AlertTriangle, title: 'Challenges', color: 'text-orange-400' },
    real_life: { icon: Users, title: 'In Real Life', color: 'text-cyan-400' },

    // ─── Activity-specific ─────────────────────────────────
    rules: { icon: Star, title: 'How to Play', color: 'text-pink-400' },
    setup: { icon: Target, title: 'Setup & Preparation', color: 'text-blue-400' },
    difficulty_scaling: { icon: Activity, title: 'Difficulty Scaling', color: 'text-orange-400' },
    props_needed: { icon: Sparkles, title: 'Props & Materials', color: 'text-amber-400' },
    time_management: { icon: Clock, title: 'Timing & Pacing', color: 'text-emerald-400' },
    partner_dynamics: { icon: Users, title: 'Partner Dynamics', color: 'text-rose-400' },
    afterplay: { icon: Heart, title: 'Afterplay & Wrap-up', color: 'text-pink-400' },
    techniques_overview: { icon: Zap, title: 'Techniques Overview', color: 'text-amber-400' },
    skill_progression: { icon: Activity, title: 'Progression Path', color: 'text-emerald-400' },
    session_planning: { icon: Clock, title: 'Session Planning', color: 'text-blue-400' },
    safety_framework: { icon: Shield, title: 'Safety Framework', color: 'text-red-400' },
    troubleshooting: { icon: Wrench, title: 'Troubleshooting', color: 'text-orange-400' },
    culture: { icon: Users, title: 'Community & Culture', color: 'text-cyan-400' },
    communication: { icon: MessageCircle, title: 'Communication', color: 'text-teal-400' },

    // ─── Position-specific ─────────────────────────────────
    transitions: { icon: Zap, title: 'Transitions', color: 'text-purple-400' },
    stamina_requirements: { icon: Activity, title: 'Stamina Needs', color: 'text-orange-400' },
    eye_contact: { icon: Eye, title: 'Eye Contact Options', color: 'text-pink-400' },
    accessibility: { icon: Heart, title: 'Accessibility', color: 'text-emerald-400' },
    ideal_for: { icon: Star, title: 'Best For', color: 'text-amber-400' },
    sensation_profile: { icon: Flame, title: 'Sensation Profile', color: 'text-rose-400' },
    tips: { icon: Sparkles, title: 'Pro Tips', color: 'text-amber-400' },

    // ─── Scenario-specific ─────────────────────────────────
    setup_guide: { icon: Target, title: 'Scene Setup', color: 'text-blue-400' },
    dialogue_examples: { icon: MessageCircle, title: 'Sample Dialogue', color: 'text-amber-400' },
    costume_suggestions: { icon: Star, title: 'Costumes & Props', color: 'text-purple-400' },
    arc: { icon: Zap, title: 'Scene Arc', color: 'text-indigo-400' },
    de_role: { icon: Heart, title: 'De-Roling', color: 'text-pink-400' },
    dialogue: { icon: Mic, title: 'Dialogue', color: 'text-amber-400' },
    escalation: { icon: Flame, title: 'Escalation', color: 'text-red-400' },
    props: { icon: Wrench, title: 'Props', color: 'text-amber-400' },
    setting: { icon: Palette, title: 'Setting', color: 'text-purple-400' },
    negotiation_template: { icon: Scale, title: 'Pre-Scene Negotiation', color: 'text-amber-400' },

    // ─── Arousal Mechanic-specific ─────────────────────────
    trigger_identification: { icon: Target, title: 'Finding Your Triggers', color: 'text-rose-400' },
    mastery: { icon: Crown, title: 'Mastery', color: 'text-purple-400' },
    techniques: { icon: Target, title: 'Techniques', color: 'text-blue-400' },
    physiology: { icon: Brain, title: 'Physiology', color: 'text-cyan-400' },
    solo_practice: { icon: Star, title: 'Solo Practice', color: 'text-pink-400' },

    // ─── Arousal Effect-specific ───────────────────────────
    experience: { icon: Flame, title: 'The Experience', color: 'text-rose-400' },
    indicators: { icon: Eye, title: 'Visible Signs', color: 'text-amber-400' },
    induction: { icon: Target, title: 'How to Induce', color: 'text-blue-400' },
    management: { icon: Shield, title: 'Managing the State', color: 'text-emerald-400' },
    recovery: { icon: Heart, title: 'Recovery', color: 'text-pink-400' },
    recognition_signs: { icon: Eye, title: 'How to Recognize', color: 'text-amber-400' },
    duration_factors: { icon: Clock, title: 'Duration Control', color: 'text-emerald-400' },
    partner_visibility: { icon: Users, title: 'Partner Cues', color: 'text-rose-400' },
    scientific_basis: { icon: BookOpen, title: 'The Science', color: 'text-cyan-400' },
    solo_exploration: { icon: Star, title: 'Solo Discovery', color: 'text-pink-400' },

    // ─── Body Part-specific ────────────────────────────────
    anatomy: { icon: Target, title: 'Anatomy', color: 'text-blue-400' },
    erogenous_mapping: { icon: Flame, title: 'Erogenous Map', color: 'text-rose-400' },
    myths: { icon: AlertTriangle, title: 'Myths Busted', color: 'text-orange-400' },
    partnered_play: { icon: Users, title: 'Partnered Play', color: 'text-pink-400' },
    care: { icon: Heart, title: 'Care & Hygiene', color: 'text-emerald-400' },
    common_issues: { icon: AlertTriangle, title: 'Common Issues', color: 'text-orange-400' },

    // ─── Character-specific (Sub Sim) ──────────────────────
    name: { icon: Mic, title: 'Name', color: 'text-cyan-400' },
    appearance: { icon: Eye, title: 'Appearance', color: 'text-pink-400' },
    archetype: { icon: Crown, title: 'Archetype', color: 'text-purple-400' },
    backstory: { icon: BookOpen, title: 'Backstory', color: 'text-indigo-400' },
    communication_style: { icon: MessageCircle, title: 'Communication Style', color: 'text-teal-400' },
    desires: { icon: Heart, title: 'Desires', color: 'text-rose-400' },
    dynamic_preference: { icon: Crown, title: 'Power Orientation', color: 'text-purple-400' },
    growth_arc: { icon: Zap, title: 'Growth Arc', color: 'text-emerald-400' },
    kinks: { icon: Flame, title: 'Kink Profile', color: 'text-rose-400' },
    limits: { icon: Shield, title: 'Limits', color: 'text-red-400' },
    personality: { icon: Brain, title: 'Personality', color: 'text-indigo-400' },
    relationships: { icon: Users, title: 'Relationships', color: 'text-pink-400' },
    scenarios: { icon: Sparkles, title: 'Scene Ideas', color: 'text-amber-400' },
    triggers: { icon: AlertTriangle, title: 'Triggers', color: 'text-red-400' },
    voice: { icon: Mic, title: 'Voice', color: 'text-amber-400' },

    // ─── Kink simulation extras ────────────────────────────
    portrayal: { icon: Film, title: 'Portrayal', color: 'text-amber-400' },
    descent_10_level: { icon: Flame, title: 'Descent Levels', color: 'text-red-400' },
    conclusion: { icon: BookOpen, title: 'Conclusion', color: 'text-blue-400' },
    glossary: { icon: BookOpen, title: 'Glossary', color: 'text-cyan-400' },
    misconceptions: { icon: AlertTriangle, title: 'Misconceptions', color: 'text-orange-400' },
    historical_precedent: { icon: Clock, title: 'Historical Context', color: 'text-cyan-400' },
    psychological_aspects: { icon: Brain, title: 'Psychology', color: 'text-indigo-400' },
    for_dominants: { icon: Crown, title: 'For Dominants', color: 'text-purple-400' },
    for_submissives: { icon: Heart, title: 'For Submissives', color: 'text-pink-400' },
    imaginary_scenarios: { icon: Sparkles, title: 'Fantasy Scenarios', color: 'text-amber-400' },
    technique_overview: { icon: Zap, title: 'Techniques', color: 'text-blue-400' },
    partner_roles: { icon: Users, title: 'Partner Roles', color: 'text-rose-400' },
    safety_notes: { icon: Shield, title: 'Safety Notes', color: 'text-red-400' },
    long_term_impact: { icon: Clock, title: 'Long-Term Impact', color: 'text-cyan-400' },
    resources: { icon: BookOpen, title: 'Resources', color: 'text-blue-400' },
    equipment: { icon: Wrench, title: 'Equipment', color: 'text-amber-400' },
    gear_required: { icon: Wrench, title: 'Required Gear', color: 'text-amber-400' }
};

/**
 * Individual section component with accordion
 */
export const PageSection = ({ sectionKey, content, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const config = SECTION_CONFIG[sectionKey] || { icon: BookOpen, title: sectionKey, color: 'text-gray-400' };
    const Icon = config.icon;

    if (!content) return null;

    return (
        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300 mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors focus:outline-none"
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <h3 className="font-fantasy font-semibold text-lg text-cream/90 tracking-wide">{config.title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-3 border-t border-wine-700/30">
                    <div className="prose prose-invert max-w-none text-cream/70 leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Render all sections from pageSections array
 * @param {Array} pageSections - Array of {sectionKey, content, contentFormatted}
 * @param {Array} prioritySections - Optional array of sectionKeys to open by default
 */
const PageSections = ({ pageSections = [], prioritySections = ['overview'] }) => {
    if (!pageSections || pageSections.length === 0) {
        return null;
    }

    // Helper to get content
    const getSectionContent = (key) => {
        const section = pageSections.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    // Get unique section keys from data
    const availableKeys = [...new Set(pageSections.map(s => s.sectionKey))];

    return (
        <div className="space-y-4">
            {availableKeys.map(key => (
                <PageSection
                    key={key}
                    sectionKey={key}
                    content={getSectionContent(key)}
                    defaultOpen={prioritySections.includes(key)}
                />
            ))}
        </div>
    );
};

export default PageSections;
